// @ts-nocheck - TODO: properly type this file!

import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import type { FormInstance } from 'antd';
import PhoneInput from 'react-phone-input-2';
import ArrowDown from '@/assets/icons/arrow-down-role.svg';
import { PhoneCountryData, HoursRow } from './types';
import { selectValueStyle } from './styles';
import styles from './SupportContactForm.module.css';
import PreferredChannelSelector, {
  PreferredChannel,
} from './PreferredChannelSelector';
import {
  CustomInput,
  AddItemButton,
  ToggleSwitch,
  CustomShipmentText,
} from '@/components';
import { dayOptions, rowsToOperatingHours, timeOptions, zones } from './utils';

type Channel = 'phone' | 'email';

type KeyedHoursRow = HoursRow & { id: number };

let rowIdCounter = 0;
const newRowId = () => {
  rowIdCounter += 1;
  return rowIdCounter;
};

type Props = {
  onSubmit?: (values) => void;
  onCancel?: () => void;
  form?: FormInstance;
  preferredChannel?: PreferredChannel | null;
  setPreferredChannel?: (v: PreferredChannel | null) => void;
};

const SupportContactForm: React.FC<Props> = ({
  onSubmit,
  preferredChannel,
  setPreferredChannel,
  form: controlledForm,
}) => {
  const makeDefaultRow = (): KeyedHoursRow => ({
    id: newRowId(),
    startDay: 'Mon',
    endDay: 'Fri',
    startTime: '09:00',
    endTime: '17:00',
  });

  const [internalForm] = Form.useForm();
  const form = controlledForm ?? internalForm;
  const [channel, setChannel] = useState<Channel>('phone');
  const [is247, setIs247] = useState(false);
  const [rows, setRows] = useState<KeyedHoursRow[]>(() => [makeDefaultRow()]);

  const phoneVal = Form.useWatch('phone_number', form);
  const tzVal = Form.useWatch('timezone', form);
  const opHoursVal = Form.useWatch('operating_hours', form);
  const hours247Val = Form.useWatch('hours_24_7', form);
  const preferredChannelVal = Form.useWatch('preferred_channel', form);

  useEffect(() => {
    const next = (preferredChannel ??
      preferredChannelVal ??
      'phone') as PreferredChannel;
    if (next === 'phone' || next === 'email') {
      setChannel(next);
    }
    form.setFieldsValue({ preferred_channel: next });
  }, [preferredChannel, preferredChannelVal, form, channel]);

  useEffect(() => {
    const h247 = Boolean(hours247Val) || opHoursVal === '24/7';
    setIs247(h247);
    if (!tzVal) {
      form.setFieldsValue({ timezone: 'GMT' });
    }
  }, [form, tzVal, opHoursVal, hours247Val]);

  useEffect(() => {
    const tz = form.getFieldValue('timezone');
    const nextOp = is247 ? '24/7' : rowsToOperatingHours(rows, tz);
    form.setFieldsValue({ hours_24_7: is247, operating_hours: nextOp });
  }, [is247, rows, form]);

  const addRow = () =>
    setRows((r) => [
      ...r,
      {
        id: newRowId(),
        startDay: null,
        endDay: null,
        startTime: null,
        endTime: null,
      },
    ]);

  const handleFinish = (values) => {
    const payload = {
      ...values,
      preferred_channel:
        values?.preferred_channel ?? preferredChannel ?? channel,
      hours_24_7: is247,
      operating_hours: is247
        ? '24/7'
        : rowsToOperatingHours(rows, values?.timezone),
    };
    onSubmit?.(payload);
  };

  const channelSpecificField = useMemo(() => {
    if (channel === 'phone') {
      return (
        <Form.Item
          label='Phone number'
          name='phone_number'
          rules={[{ required: true, message: 'Please enter phone number' }]}
          style={{ marginBottom: 16 }}
        >
          <CustomInput
            height='36px'
            bgColor='var(--light-gray)'
            border='0px'
            borderRadius='8px'
          >
            <PhoneInput
              country={'us'}
              value={phoneVal || ''}
              inputStyle={{
                height: 36,
                width: '100%',
                border: 'none',
                background: 'transparent',
                paddingLeft: 48,
              }}
              buttonStyle={{
                background: 'transparent',
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                border: 'none',
              }}
              onChange={(
                value,
                data: PhoneCountryData,
                _event,
                formattedValue
              ) => {
                form.setFieldsValue({
                  phone_number: formattedValue || value,
                  country_code: data?.dialCode ? `+${data.dialCode}` : '',
                  country_region: data?.countryCode
                    ? String(data.countryCode).toUpperCase()
                    : '',
                });
              }}
            />
          </CustomInput>
        </Form.Item>
      );
    }

    return (
      <>
        <div className={styles.emailLabel}>
          Email <span className={styles.required}>*</span>
        </div>
        <Form.Item
          name='email'
          rules={[
            { required: true, message: 'Please enter email' },
            { type: 'email' as const, message: 'Please enter a valid email' },
          ]}
          style={{ marginBottom: 16 }}
        >
          <CustomInput
            placeholder='name@company.com'
            height='36px'
            border='0px'
            bgColor='var(--light-gray)'
            borderRadius='8px'
          />
        </Form.Item>
      </>
    );
  }, [channel, phoneVal, form]);

  return (
    <div className={styles.root}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        initialValues={{ timezone: 'GMT' }}
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        <Form.Item name='country_code' hidden>
          <input type='hidden' />
        </Form.Item>
        <Form.Item name='country_region' hidden>
          <input type='hidden' />
        </Form.Item>
        <Form.Item name='preferred_channel' hidden>
          <input type='hidden' />
        </Form.Item>
        <Form.Item name='hours_24_7' hidden>
          <input type='hidden' />
        </Form.Item>
        <Form.Item name='operating_hours' hidden>
          <input type='hidden' />
        </Form.Item>

        <div className={styles.labelGroup}>
          <Form.Item
            className='custom-input form-input-style'
            label='Label'
            name='label'
            rules={[{ required: true, message: 'Please enter label' }]}
          >
            <CustomInput
              placeholder='i.e., Main, Dangerous Goods, Express Freight, Billing'
              height='36px'
              border='0px'
              bgColor='var(--light-gray)'
              borderRadius='8px'
            />
          </Form.Item>
        </div>

        <PreferredChannelSelector
          title='Channel'
          value={preferredChannel ?? preferredChannelVal ?? channel ?? 'phone'}
          onChange={(val) => {
            setPreferredChannel?.(val);
            if (val === 'phone' || val === 'email') {
              setChannel(val);
              if (val === 'phone') {
                form.setFieldsValue({ email: '' });
              } else {
                form.setFieldsValue({
                  phone_number: '',
                  country_code: '',
                  country_region: '',
                });
              }
            }
          }}
          isDirect={false}
          style={{ marginTop: -4, marginBottom: 8 }}
        />

        {channelSpecificField}

        <div className={styles.hoursHeader}>
          <div className={styles.label}>Operating hours</div>
          <div className={styles.toggleWrap}>
            <CustomShipmentText text='24/7' lineHeight='20px' />
            <ToggleSwitch
              checked={is247}
              onChange={(e) => {
                const checked = e.target.checked;
                setIs247(checked);

                if (checked) {
                  setRows([]);
                  form.setFieldsValue({
                    hours_24_7: true,
                    operating_hours: '24/7',
                  });
                  return;
                }

                const nextRows = rows.length ? rows : [makeDefaultRow()];
                setRows(nextRows);

                const tz = form.getFieldValue('timezone');
                form.setFieldsValue({
                  hours_24_7: false,
                  operating_hours: rowsToOperatingHours(nextRows, tz),
                });
              }}
            />
          </div>
        </div>

        <div className={styles.rowsWrap}>
          {!is247 &&
            rows.map((row, idx) => (
              <Row
                key={row.id}
                gutter={[8, 0]}
                align='middle'
                wrap={false}
                style={{ marginBottom: 8, flexWrap: 'nowrap', width: '100%' }}
              >
                <Col flex='1 1 0'>
                  <Select
                    placeholder='Mon'
                    value={row.startDay ?? undefined}
                    onChange={(val) => {
                      const next = [...rows];
                      next[idx].startDay = val;
                      setRows(next);
                    }}
                    options={dayOptions}
                    suffixIcon={
                      <img
                        src={ArrowDown}
                        width={12}
                        height={12}
                        className={styles.arrowIcon}
                      />
                    }
                    className='select36'
                    style={{
                      ...selectValueStyle,
                      background: 'var(--light-gray)',
                      borderRadius: 8,
                      width: '100%',
                      minWidth: 60,
                      height: 36,
                    }}
                    disabled={is247}
                  />
                </Col>

                <Col
                  flex='0 0 18px'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span className={styles.toText}>to</span>
                </Col>

                <Col flex='1 1 0'>
                  <Select
                    placeholder='Fri'
                    value={row.endDay ?? undefined}
                    onChange={(val) => {
                      const next = [...rows];
                      next[idx].endDay = val;
                      setRows(next);
                    }}
                    options={dayOptions}
                    suffixIcon={
                      <img
                        src={ArrowDown}
                        width={12}
                        height={12}
                        className={styles.arrowIcon}
                      />
                    }
                    className='select36'
                    style={{
                      ...selectValueStyle,
                      background: 'var(--light-gray)',
                      borderRadius: 8,
                      width: '100%',
                      minWidth: 60,
                      height: 36,
                    }}
                    disabled={is247}
                  />
                </Col>

                <Col flex='1 1 0'>
                  <Select
                    placeholder='9am'
                    value={row.startTime ?? undefined}
                    onChange={(val) => {
                      const next = [...rows];
                      next[idx].startTime = val;
                      setRows(next);
                    }}
                    options={timeOptions}
                    suffixIcon={
                      <img
                        src={ArrowDown}
                        width={12}
                        height={12}
                        className={styles.arrowIcon}
                      />
                    }
                    className='select36'
                    style={{
                      ...selectValueStyle,
                      background: 'var(--light-gray)',
                      borderRadius: 8,
                      width: '100%',
                      minWidth: 60,
                      height: 36,
                    }}
                    disabled={is247}
                  />
                </Col>

                <Col
                  flex='0 0 18px'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span className={styles.toText}>to</span>
                </Col>

                <Col flex='1 1 0' style={{ paddingRight: 0 }}>
                  <Select
                    placeholder='5'
                    value={row.endTime ?? undefined}
                    onChange={(val) => {
                      const next = [...rows];
                      next[idx].endTime = val;
                      setRows(next);
                    }}
                    options={timeOptions}
                    suffixIcon={
                      <img
                        src={ArrowDown}
                        width={12}
                        height={12}
                        className={styles.arrowIcon}
                      />
                    }
                    className='select36'
                    style={{
                      ...selectValueStyle,
                      background: 'var(--light-gray)',
                      borderRadius: 8,
                      width: '100%',
                      minWidth: 60,
                      height: 36,
                    }}
                    disabled={is247}
                  />
                </Col>
              </Row>
            ))}

          {!is247 && (
            <AddItemButton
              gap='24px'
              width='28px'
              height='28px'
              marginTop='0px'
              text='Add days'
              fontSize='12px'
              fontWeight='600'
              onClick={addRow}
            />
          )}
        </div>

        <Form.Item
          label='Timezone'
          name='timezone'
          style={{ marginBottom: 16, marginTop: 16 }}
        >
          <Select
            showSearch
            placeholder='Select Time Zone'
            className='custom-dd'
            optionFilterProp='label'
            options={zones.map((zone) => ({ label: zone, value: zone }))}
            style={{ width: '100%' }}
            onChange={(tz) => {
              form.setFieldsValue({
                operating_hours: is247
                  ? '24/7'
                  : rowsToOperatingHours(rows, tz),
              });
            }}
          />
        </Form.Item>

        <Form.Item label='Note' name='notes' style={{ marginTop: 16 }}>
          <Input.TextArea
            rows={3}
            placeholder=''
            style={{
              height: '120px',
              paddingTop: 10,
              resize: 'none',
              background: 'var(--light-gray)',
              border: 'none',
              boxShadow: 'none',
            }}
            value={form.getFieldValue('notes')}
            onChange={(e) => {
              const v = e.target.value;
              form.setFieldsValue({ notes: v });
            }}
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default SupportContactForm;
